const _ = require("lodash");
const moment = require("moment");

const ArrayUtil = require("../../core/util/ArrayUtil");
const StoreUtil = require("../../../shared/StoreUtil");
const {setIn, extendIn} = require("../../../shared/ImmHelpers");
const {FLAGS} = require("../../core/util/GlobalFlags");
const AT = require("../../core/vault/ActionTypes");
const {
	addDevTestingShortcutHandler,
} = require("../../core/util/setupDevTestingShortcut");

// Form shape {
//   mode: "cart" | "parent" | "subChange",
//   data: {contact, account, shipping, billing, creditCard},
//   validation: {contact, account, shipping, billing, creditCard},
//   shippingForBilling,
// }

const knownModes = ["cart", "parent", "subChange"];

const init = function(mode, now) {
	if (knownModes.indexOf(mode) === -1) {
		throw new Error("E_INVALID_FORM_MODE");
	}
	const initialData = {
		contact: {
			phone: "",
			email: "",
			comments: "",
		},
		shipping: {
			name: "",
			country: StoreUtil.USA,
			address1: "",
			address2: "",
			city: "",
			state: "",
			zipcode: "",
			method: "",
			comments: "",
			taxpayerID: "",
		},
		billing: {
			name: "",
			country: StoreUtil.USA,
			address1: "",
			address2: "",
			city: "",
			state: "",
			zipcode: "",
		},
		creditCard: {
			number: "",
			cvv: "",
			expMonth: moment(now).month() + 1,
			expYear: moment(now).year(),
			agreeRecurring: false,
		},
	};
	if (mode === "cart") {
		initialData.account = {
			ownerID: null,
			email: "",
			emailRepeat: "",
			password: "",
			passwordRepeat: "",
			exists: false,
			remember: false,
		};
	}

	const form = {
		mode,
		data: initialData,
		validation: null,
		shippingForBilling: mode === "cart",
	};
	form.validation = _.mapValues(form.data, (info, section) => {
		return StoreUtil.checkSection(getSection(form, section), {
			skipFields: getSkipFieldsForSection(section),
		});
	});
	return form;
};

let fakeCheckoutFormData;
if (process.env.NODE_ENV !== "production") {
	const fakeEmailHash = moment().format("X");
	fakeCheckoutFormData = {
		contact: {
			phone: "1234567890",
			email: "kcobbe@aopstest.com",
			comments: "This is a comment.",
		},
		account: {
			ownerID: null,
			email: `${fakeEmailHash}@aopstest.com`,
			emailRepeat: `${fakeEmailHash}@aopstest.com`,
			password: "AwesomeMouse11",
			passwordRepeat: "AwesomeMouse11",
			exists: false,
			remember: true,
		},
		// shipping: {
		//   name: "karl morrisville",
		//   country: StoreUtil.USA,
		//   address1: "6406 McCrimmon Pkwy",
		//   address2: "Ste 100",
		//   city: "Morrisville",
		//   state: "NC",
		//   zipcode: "27560",
		//   method: "us-economy",
		// },
		shipping: {
			name: "Carmen San Diego",
			country: StoreUtil.USA,
			address1: "15330 Avenue of Science",
			address2: "",
			city: "San Diego",
			state: "CA",
			zipcode: "92128",
			method: "us-economy",
			comments: "",
			taxpayerID: "",
		},
		// shipping: {
		//   name: "karl",
		//   country: StoreUtil.CANADA,
		//   address1: "4151 Rue Saint-Hubert",
		//   address2: "",
		//   city: "Montreal",
		//   state: "QC",
		//   zipcode: "H2J1X8",
		//   method: "canada",
		// },
		billing: {
			name: "karl",
			country: StoreUtil.USA,
			address1: "15330 Avenue of Science",
			address2: "",
			city: "San Diego",
			state: "CA",
			zipcode: "92128",
		},
		creditCard: {
			number: "4111111111111111",
			cvv: "123",
			expMonth: 1,
			expYear: moment()
				.add(1, "year")
				.format("YYYY"),
		},
	};
}

const copyShippingToBilling = function(form) {
	return setData(
		form,
		"billing",
		_.omit(getSection(form, "shipping"), ["method", "comments"])
	);
};

const setShippingAsBilling = function(form, value, options = {}) {
	value = !!value;
	if (form.shippingForBilling === value) return form;
	form = setIn(form, ["shippingForBilling"], value);
	// Update validation information
	const newBillingValidation = value
		? form.validation.shipping
		: StoreUtil.checkSection(form.data.billing);
	form = setIn(form, ["validation", "billing"], newBillingValidation);
	// Re-validate contact (as the phone requirements may have changed)
	const {shouldMovePhoneNumber = false} = options;
	form = validateSection(form, "contact", {shouldMovePhoneNumber});
	return form;
};

const getSectionNames = function(form) {
	const ret = ["shipping", "billing", "creditCard", "contact"];
	if (form.mode === "cart") {
		ret.push("account");
	}
	return ret;
};

// Sets data without doing any checks on it
const setData = function(form, section, vals) {
	if (!vals) return form;
	form = extendIn(form, ["data", section], vals);
	return form;
};

const updateData = function(form, section, vals, options = {}) {
	const {
		// Whether to use shipping or billing for tax computations
		useBillingForTaxes = false,
		needsRecurringBilling = false,
		shouldSkipPhoneValidation = false,
	} = options;

	const responseFlags = {
		hadUpdates: false,
		updatePrices: false,
		verifyAddress: false,
		verifyAccount: false,
	};
	if (!vals) {
		return {form, responseFlags};
	}

	if (
		fakeCheckoutFormData &&
		FLAGS.allowTestCheckoutForms &&
		_.includes(_.values(vals), "test")
	) {
		const contactEmail = _.get(form, "data.contact.email");
		if (section === "contact" && contactEmail) {
			vals = {
				...fakeCheckoutFormData.contact,
				email: contactEmail,
			};
		} else {
			vals = fakeCheckoutFormData[section];
		}
	}
	const oldForm = form;
	form = extendIn(form, ["data", section], vals);
	if (oldForm === form) {
		return {form, responseFlags};
	}

	responseFlags.hadUpdates = true;
	let shouldValidateContact = false;
	// New values to extend in form.data[section]
	const newFormValues = {};

	const oldVals = _.get(oldForm, ["data", section]);
	const newVals = _.get(form, ["data", section]);

	const oldCountry = oldVals.country;
	const newCountry = newVals.country;
	if (
		oldCountry !== newCountry &&
		(StoreUtil.isCountryCanada(oldCountry) ||
			StoreUtil.isCountryUS(oldCountry) ||
			StoreUtil.isCountryCanada(newCountry) ||
			StoreUtil.isCountryUS(newCountry))
	) {
		//If switching to or from a dropdown list of states, clear the current state value
		newFormValues.state = vals.state || "";
		if (
			section === "billing" ||
			(section === "shipping" && form.shippingForBilling)
		) {
			shouldValidateContact = true; //Phone checks change when switching from/to the US & Canada
		}
	}
	if (StoreUtil.isCountryUS(newCountry) && oldVals.state !== newVals.state) {
		shouldValidateContact = true; //Phone checks change when switching from/to US territories vs. states
	}
	if (section === "shipping") {
		//Check if the shipping method needs to be updated based on changes to certain fields
		if (oldCountry !== newCountry) {
			newFormValues.method = StoreUtil.getDefaultShippingType(newCountry);
		}
		if (oldVals.state !== newVals.state) {
			if (StoreUtil.isCountryUS(form.data.shipping.country)) {
				if (StoreUtil.requiresPriorityShipping(newVals.state)) {
					newFormValues.method = "us-priority";
				} else if (StoreUtil.requiresPriorityShipping(oldVals.state)) {
					newFormValues.method = "";
				}
			}
		}
		if (oldVals.method !== newVals.method) {
			responseFlags.updatePrices = true;
		}
		const oldAddressVals = _.omit(oldVals, ["name", "method"]);
		const newAddressVals = _.omit(newVals, ["name", "method"]);
		if (!_.isEqual(oldAddressVals, newAddressVals)) {
			//If anything but the method changed, the address will need to be verified again.
			responseFlags.verifyAddress = true;
			if (!useBillingForTaxes) {
				responseFlags.updatePrices = true;
			}
		}
	}
	if (useBillingForTaxes && section === "billing") {
		const oldAddressVals = _.omit(oldVals, ["name"]);
		const newAddressVals = _.omit(newVals, ["name"]);
		if (!_.isEqual(oldAddressVals, newAddressVals)) {
			responseFlags.updatePrices = true;
		}
	}
	if (section === "account") {
		const oldAccountVals = _.omit(oldVals, ["remember"]);
		const newAccountVals = _.omit(newVals, ["remember"]);
		if (!_.isEqual(oldAccountVals, newAccountVals)) {
			responseFlags.verifyAccount = true;
		}
		if (newVals.email) {
			form = setIn(form, ["data", "contact", "email"], newVals.email);
		}
	}
	if (
		section === "creditCard" &&
		needsRecurringBilling &&
		!newVals.agreeRecurring
	) {
		newFormValues.agreeRecurring = false;
	}
	form = extendIn(form, ["data", section], newFormValues);
	form = validateSection(form, section, {
		needsRecurringBilling,
		shouldSkipPhoneValidation,
	});
	if (shouldValidateContact) {
		form = validateSection(form, "contact", {shouldSkipPhoneValidation});
	}

	return {form, responseFlags};
};

const setupCheckoutShortcut = (form, dispatch) => {
	if (!fakeCheckoutFormData || !FLAGS.allowTestCheckoutForms) {
		return;
	}
	const handleShortcut = () => {
		Object.keys(fakeCheckoutFormData).forEach(section => {
			const vals = fakeCheckoutFormData[section];
			dispatch({
				type: AT.UPDATE_CART_FORMDATA,
				section,
				vals,
			});
		});
	};
	const teardown = addDevTestingShortcutHandler(handleShortcut);
	return teardown;
};

const getSection = function(form, section) {
	if (section === "billing" && form.shippingForBilling) {
		return _.omit(form.data.shipping, ["method"]);
	} else {
		return form.data[section];
	}
};

// Get a canonical zipcode, only for US addresses. By default, it will check
// shipping, then billing. Pass section to have it check just that section.
const getUSZipCode = function(form, section) {
	const sectionsToCheck = section ? [section] : ["shipping", "billing"];
	const trySection = sectionToTry => {
		const formSection = form.data[sectionToTry];
		if (formSection && formSection.country !== "US") return null;
		return formSection.zipcode || null;
	};

	const zipcode = ArrayUtil.getFirstMappedEl(sectionsToCheck, trySection);
	return zipcode;
};

// section can be "shipping" or "billing".
const setVerifiedAddress = function(form, section, rawAddress) {
	// We intentionally ignore everything but the state and zipcode.
	if (!rawAddress) return form;
	let newAddress = _.extend({}, form.data[section], {
		state: rawAddress.state || form.data[section].state,
		zipcode: rawAddress.zipcode || form.data[section].zipcode,
	});
	newAddress = _.mapValues(newAddress, v => v || "");
	return setData(form, section, newAddress);
};

const validateSection = function(form, section, options = {}) {
	const {
		addMissing = false,
		methodOnly = false, // Only for section shipping
		needsRecurringBilling = false,
		shouldSkipPhoneValidation = false,
	} = options;
	let sectionData = getSection(form, section);
	// contact information needs to be sent with billing country and state
	let skipFields = options.skipFields || {};
	const additionalRequiredFields = {};
	if (section === "contact") {
		// We need the state/country to test the phone #, but don't want to
		// actually check them for validity.
		const billing = getSection(form, "billing");
		sectionData = _.extend({}, sectionData, {
			country: billing.country,
			state: billing.state,
		});
		skipFields = _.extend({country: true, state: true}, skipFields);
		// Skip phone validation if phone number should be moved to account step
		if (shouldSkipPhoneValidation) {
			skipFields = _.extend({phone: true}, skipFields);
		}
	}
	if (form.mode === "parent") {
		skipFields = _.extend({method: true}, skipFields);
	}
	skipFields = _.extend(getSkipFieldsForSection(section), skipFields);
	if (methodOnly && section === "shipping") {
		sectionData = {method: sectionData.method};
	}
	if (section === "creditCard" && needsRecurringBilling) {
		additionalRequiredFields.agreeRecurring = true;
	}
	const validation = StoreUtil.checkSection(sectionData, {
		addMissing,
		origErrorTypes: _.get(form, ["validation", section, "errorTypes"]),
		skipFields,
		additionalRequiredFields,
	});
	form = setIn(form, ["validation", section], validation);
	// if billing uses shipping address, also copy the validation from shipping
	if (section === "shipping" && form.shippingForBilling) {
		form = setIn(form, ["validation", "billing"], validation);
	}
	return form;
};

const setSectionInvalidAddress = function(form, section, value) {
	return setIn(form, ["validation", section, "invalidAddressGroup"], value);
};

// Central spot for skipping fields not needed for validation in different sections
const getSkipFieldsForSection = function(section) {
	const skipFields = {};
	if (section === "account") {
		// Skip emailRepeat validation - users don't need to enter email twice
		skipFields.emailRepeat = true;
	}
	return skipFields;
};

const checkSection = function(form, section, options = {}) {
	const {
		methodOnly = false, // Only for section shipping
	} = options;
	if (methodOnly && section === "shipping") {
		const {method} = form.data.shipping;
		return StoreUtil.checkSection(
			{method},
			{
				origErrorTypes: form.validation.shipping.errorTypes,
			}
		);
	}
	return form.validation[section];
};

const isSectionComplete = function(form, section, options) {
	return checkSection(form, section, options).isComplete;
};

// now is only needed if resetting the credit card section.
const resetSection = function(form, section, now) {
	if (!form) {
		return form;
	}
	const resetForm = init(form.mode, now);
	form = setIn(form, ["data", section], resetForm.data[section]);
	const newValidation = StoreUtil.checkSection(getSection(form, section), {
		skipFields: getSkipFieldsForSection(section),
	});
	form = setIn(form, ["validation", section], newValidation);
	return form;
};

// Returns a combined validation object for multiple form sections. This
// doesn't handle overlapping field names well, so e.g. don't pass both
// shipping and billing in formTypes if you don't want some things overwritten.
const getGroupedValidation = function(form, formTypes) {
	const formVs = formTypes.map(t => form.validation[t]);
	const errorTypes = {};
	_.each(formVs, f => {
		_.extend(errorTypes, f.errorTypes);
	});
	return {
		missing: _.union(...formVs.map(x => x.missing)),
		invalid: _.union(...formVs.map(x => x.invalid)),
		isComplete: _.every(formVs.map(x => x.isComplete)),
		errorTypes,
		invalidAddressGroup: _.some(formVs.map(x => x.invalidAddressGroup)),
	};
};

module.exports = {
	init,
	getSectionNames,
	setData,
	updateData,
	setupCheckoutShortcut,
	copyShippingToBilling,
	setShippingAsBilling,
	getSection,
	getUSZipCode,
	setVerifiedAddress,
	validateSection,
	setSectionInvalidAddress,
	checkSection,
	isSectionComplete,
	resetSection,
	getGroupedValidation,
};
